import * as React from "react";
import IdleTimer from "react-idle-timer";
import { RouteComponentProps } from "react-router-dom";
import { Helmet } from "react-helmet";

import { DataValue, graphql, withApollo, ApolloClient } from "../react-apollo";
import { SessionProps, withSession } from "../session";
import {
  GameQuery,
  gameQuery,
  gameMove,
  gameStartDuel,
  gameSkipDuel,
  gameResolveDuel,
  gameResign,
} from "../graphql/game";
import GameState, { Color } from "../board/GameState";
import GameInterface from "../board/GameInterface";
import { Grid } from "../semantic-ui";
import GameInfo from "./GameInfo";
import SoundEffect from "../common/SoundEffect";
import { publicUrl } from "../urls";

type RouteMatch = { id: string };
type Props = RouteComponentProps<RouteMatch> &
  SessionProps & {
    client: ApolloClient<any>;
    data?: DataValue<GameQuery>;
  };

type State = {
  currentActingColor: Color | undefined;
  soundEnabled: boolean;
};

class GamePage extends React.Component<Props> {
  sound = new SoundEffect(publicUrl("/notification.mp3"));
  idleTimer: IdleTimer | undefined;
  // The pending setTimeout for the next refresh
  refreshTimeout: NodeJS.Timeout | undefined;
  state = { currentActingColor: undefined, soundEnabled: true };

  componentDidMount() {
    this.scheduleRefresh();
  }

  componentDidUpdate() {
    const { game } = this.props.data!;
    const nowActingColor = game ? game.toAct : this.state.currentActingColor;
    const toActChanged =
      this.state.currentActingColor &&
      this.state.currentActingColor !== nowActingColor;
    const actorIsMe = this.viewerColor() === nowActingColor;
    if (toActChanged) {
      this.setState({ currentActingColor: nowActingColor });
    }
    if (toActChanged && actorIsMe && this.state.soundEnabled) {
      this.sound.play();
    }
  }

  componentWillUnmount() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  viewerColor() {
    const { data: { game } = this.props.data!, me } = this.props;
    if (!game || !me) return undefined;
    return me && me.id === game.white.id
      ? "white"
      : me && me.id === game.black.id
      ? "black"
      : undefined;
  }

  render() {
    const {
      data: { game, loading, error } = this.props.data!,
      sessionQuery,
    } = this.props;
    if (loading || sessionQuery.loading) return <div>Loading...</div>;
    if (error) {
      return (
        <div>
          <strong>Error:</strong> {error.message}
        </div>
      );
    } else if (!game) {
      return <div>Not found</div>;
    } else {
      const gameState = GameState.fromGame(game);
      const viewerColor = this.viewerColor();
      return (
        <div>
          <IdleTimer
            ref={(e) => (this.idleTimer = e!)}
            timeout={1000 * 60}
            onActive={this.scheduleRefresh}
          />
          <Helmet
            title={
              game.winner
                ? "Online Game"
                : viewerColor === game.toAct
                ? "Your Turn | Online Game"
                : viewerColor
                ? "Waiting | Online Game"
                : "Online Game"
            }
          />
          <Grid stackable centered>
            <Grid.Column width={8} style={{ maxWidth: 600 }}>
              <GameInterface
                game={gameState}
                nextAction={game.nextAction}
                pendingDuelBid={game.pendingDuelBid}
                viewerColor={viewerColor}
                flipped={viewerColor === "black"}
                onMove={this.handleMove}
                onStartDuel={this.handleStartDuel}
                onSkipDuel={this.handleSkipDuel}
                onResolveDuel={this.handleResolveDuel}
              />
            </Grid.Column>
            <Grid.Column width={8} style={{ maxWidth: 600 }}>
              <GameInfo
                game={game}
                gameState={gameState}
                viewerColor={viewerColor}
                soundEnabled={this.state.soundEnabled}
                onResign={this.handleResign}
                onEnableSound={this.handleEnableSound}
              />
            </Grid.Column>
          </Grid>
        </div>
      );
    }
  }

  scheduleRefresh = () => {
    if (this.refreshTimeout) return;
    this.refreshTimeout = setTimeout(() => {
      // Before refetching, store the "current acting color", to detect if it
      // changes
      const { game } = this.props.data!;
      if (game) {
        this.setState({ currentActingColor: game.toAct });
      }
      this.props.data!.refetch();
      this.refreshTimeout = undefined;
      if (this.idleTimer && !this.idleTimer.isIdle()) {
        this.scheduleRefresh();
      }
    }, 10000);
  };

  handleMove = (uci: string) => {
    this.props.client.mutate({
      mutation: gameMove,
      variables: { id: this.props.data!.game!.id, uci },
    });
  };

  handleStartDuel = (duelNumber: number, bid: number) => {
    this.props.client.mutate({
      mutation: gameStartDuel,
      variables: { id: this.props.data!.game!.id, duelNumber, bid },
    });
  };

  handleSkipDuel = (duelNumber: number) => {
    this.props.client.mutate({
      mutation: gameSkipDuel,
      variables: { id: this.props.data!.game!.id, duelNumber },
    });
  };

  handleResolveDuel = (bid: number, gain: boolean | void) => {
    this.props.client.mutate({
      mutation: gameResolveDuel,
      variables: { id: this.props.data!.game!.id, bid, gain },
    });
  };

  handleResign = () => {
    this.props.client.mutate({
      mutation: gameResign,
      variables: { id: this.props.data!.game!.id },
    });
  };

  handleEnableSound = (soundEnabled: boolean) => {
    this.setState({ soundEnabled });
  };
}

const withGameQuery = graphql<RouteComponentProps<RouteMatch>, GameQuery>(
  gameQuery,
  {
    alias: "withGameQuery",
    options: (props) => ({
      variables: { id: props.match.params.id },
    }),
  }
);

export default withGameQuery(withSession(withApollo(GamePage)));
